import React, {
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
} from 'react';
import { Plus } from 'lucide-react';
import {
  GridPaginationModel,
  GridSortModel,
  GridFilterModel,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid-premium';
import useAuth from '../auth/auth';
import { Grade } from '../services/api/domains/grades/types';
import {
  useGradesPage,
  useMergeSuggestions,
  useMergePreview,
  useMergeGrades,
} from '../services/api/domains/grades/hooks';
import GradeModal from '../components/modals/GradeModal';
import GenericMergeModal from '../components/modals/GenericMergeModal';
import { ModalMode } from '../components/modals/hooks/useEditableModal';
import {
  UnifiedSearchBar,
  UnifiedSearchState,
} from 'components/UnifiedSearchBar';
import {
  AdvancedSearchQuery,
  createEmptyAdvancedQuery,
} from 'components/TableSearch/types';
import { GRADE_PRESETS } from '../domains/grades/filterPresets';
import {
  mergeAdvancedQueries,
  gridFilterToAdvancedQuery,
} from '../utils/dataGridHelpers';
import { gradesFieldConfig } from '../config/searchConfigs';
import LgmDataGrid from 'components/LgmDataGrid';
import { DensityToggle } from 'components/DensityToggle';
import { useTableDensity } from 'shared/hooks/useTableDensity';
import { useSessionStorageState } from 'shared/hooks/useSessionStorageState';
import {
  toDataGridColumns,
  getDefaultColumnVisibility,
} from '../domains/grades/adapters';
import { GRAY_300 } from 'theme/grayScale';
import {
  STORAGE_KEY_GRADES_FILTER_ACTIVE,
  STORAGE_KEY_GRADES_FILTER_PRESETS,
  STORAGE_KEY_GRADES_GRID_FILTER,
} from 'utils/storageKeys';

const PAGE_SIZE = 50;

const Grades: React.FC = () => {
  const { isAdmin, userId, isViewer } = useAuth();
  const { density } = useTableDensity();
  const rowHeight = density === 'compact' ? 36 : 52;

  // Search state — managed by UnifiedSearchBar, reflected here for API params
  const [searchState, setSearchState] = useState<UnifiedSearchState>({
    mode: 'basic',
    basicTerm: '',
    advancedQuery: createEmptyAdvancedQuery(),
  });
  const pageRef = useRef(0);

  // DataGrid state
  const [gridFilterModel, setGridFilterModel] =
    useSessionStorageState<GridFilterModel>(STORAGE_KEY_GRADES_GRID_FILTER, {
      items: [],
    });
  const [gridSortModel, setGridSortModel] = useState<GridSortModel>([]);
  const [gridPaginationModel, setGridPaginationModel] =
    useState<GridPaginationModel>({
      page: 0,
      pageSize: PAGE_SIZE,
    });
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(getDefaultColumnVisibility());

  const gradesParams = useMemo(() => {
    // Convert DataGrid filters to AdvancedSearchQuery
    const gridAdvancedQuery = gridFilterToAdvancedQuery(
      gridFilterModel,
      gradesFieldConfig
    );

    // Merge with existing search
    let finalAdvancedSearch: AdvancedSearchQuery | undefined;
    if (searchState.mode === 'advanced') {
      finalAdvancedSearch = mergeAdvancedQueries(
        searchState.advancedQuery,
        gridAdvancedQuery
      );
    } else if (gridAdvancedQuery.filterGroups.length > 0) {
      finalAdvancedSearch = gridAdvancedQuery;
    }

    return {
      search:
        searchState.mode === 'basic'
          ? searchState.basicTerm.trim() || undefined
          : undefined,
      advancedSearch: finalAdvancedSearch,
      sortBy: gridSortModel[0]?.field as keyof Grade | undefined,
      sortDirection: gridSortModel[0]?.sort?.toUpperCase() as
        'ASC' | 'DESC' | undefined,
      limit: gridPaginationModel.pageSize,
      offset: gridPaginationModel.page * gridPaginationModel.pageSize,
    };
  }, [searchState, gridFilterModel, gridSortModel, gridPaginationModel]);

  const {
    create: createGrade,
    update: updateGrade,
    delete: deleteGrade,
    data: grades,
    total,
    refetch: refetchGrades,
  } = useGradesPage(gradesParams);

  // Preserve total during loading to prevent DataGrid from resetting pagination
  const [stableTotal, setStableTotal] = useState(total || 0);
  useEffect(() => {
    if (total !== undefined && total > 0) {
      setStableTotal(total);
    }
  }, [total]);

  // Get DataGrid columns from adapter
  const gradesColumns = useMemo(() => toDataGridColumns(), []);

  const [selectedGrade, setSelectedGrade] = useState<Grade | null>(null);
  const [openGradeModal, setOpenGradeModal] = useState<ModalMode | null>(null);
  const [openMergeModal, setOpenMergeModal] = useState(false);
  const [mergeSourceGrade, setMergeSourceGrade] = useState<Grade | null>(null);

  // Reset page when search changes
  useEffect(() => {
    if (pageRef.current !== 0) {
      setGridPaginationModel(prev => ({ ...prev, page: 0 }));
    }
  }, [searchState]);

  useEffect(() => {
    pageRef.current = gridPaginationModel.page;
  }, [gridPaginationModel.page]);

  const handleSearchChange = useCallback((state: UnifiedSearchState) => {
    setSearchState(state);
  }, []);

  const handleSaveGrade = useCallback(
    async (gradeData: Grade) => {
      try {
        if (gradeData.grade_id) {
          await updateGrade.mutate({
            grade_id: gradeData.grade_id,
            grade_name: gradeData.grade_name,
            user_id: userId,
          });
        } else {
          await createGrade.mutate({
            grade_name: gradeData.grade_name,
            user_id: userId,
          });
        }
        setSelectedGrade(null);
        setOpenGradeModal(null);
        refetchGrades();
        alert('✅ Product Type saved successfully');
      } catch (err) {
        console.error('❌ Save failed:', err);
        alert('❌ Failed to save product type. Please try again.');
      }
    },
    [updateGrade, createGrade, refetchGrades, userId]
  );

  const handleDelete = useCallback(
    async (id: number) => {
      if (!window.confirm('Delete this product type?')) return;
      try {
        await deleteGrade.mutate(id);
        refetchGrades();
        alert('✅ Product Type deleted successfully');
      } catch (err) {
        console.error('❌ Delete failed:', err);
        alert('❌ Failed to delete product type. Please try again.');
      }
    },
    [deleteGrade, refetchGrades]
  );

  // Memoized handlers for modal actions
  const handleAddGrade = useCallback(() => {
    const newGrade: Grade = {
      grade_id: 0,
      grade_name: '',
      created_date: new Date(),
      modified_date: new Date(),
      user_id: userId,
    };
    setSelectedGrade(newGrade);
    setOpenGradeModal('EDIT');
  }, [userId]);

  const handleCloseGradeModal = useCallback(() => {
    setSelectedGrade(null);
    setOpenGradeModal(null);
  }, []);

  const handleOpenMergeModal = useCallback((grade: Grade) => {
    setMergeSourceGrade(grade);
    setOpenMergeModal(true);
  }, []);

  const handleCloseMergeModal = useCallback(() => {
    setMergeSourceGrade(null);
    setOpenMergeModal(false);
  }, []);

  const handleMergeSuccess = useCallback(() => {
    refetchGrades();
    alert('✅ Product Types merged successfully');
  }, [refetchGrades]);

  return (
    <div style={styles.pageContainerStyle}>
      <div
        style={styles.searchBarContainerStyle}
        data-layout-role="search-panel">
        <UnifiedSearchBar
          fieldConfigs={gradesFieldConfig}
          placeholder="Search product types..."
          builtInPresets={GRADE_PRESETS}
          storageKeyUserPresets={STORAGE_KEY_GRADES_FILTER_PRESETS}
          storageKeyActivePreset={STORAGE_KEY_GRADES_FILTER_ACTIVE}
          defaultPreset={GRADE_PRESETS.find(p => p.id === 'grade_all')}
          onSearchChange={handleSearchChange}
          actions={
            <>
              <DensityToggle />
              {isAdmin && (
                <button onClick={handleAddGrade} style={styles.addButtonStyle}>
                  <Plus size={16} /> Add Product Type
                </button>
              )}
            </>
          }
        />
      </div>

      <div style={styles.tableContainerStyle} data-layout-role="table-body">
        <LgmDataGrid<Grade>
          data={grades}
          columns={gradesColumns}
          rowCount={stableTotal}
          rowHeight={rowHeight}
          paginationModel={gridPaginationModel}
          onPaginationModelChange={setGridPaginationModel}
          sortModel={gridSortModel}
          onSortModelChange={setGridSortModel}
          filterModel={gridFilterModel}
          onFilterModelChange={setGridFilterModel}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          getRowId={row => row.grade_id}
          onInfo={(g: Grade) => {
            setSelectedGrade(g);
            setOpenGradeModal('VIEW');
          }}
          onEdit={
            !isViewer
              ? (g: Grade) => {
                  setSelectedGrade(g);
                  setOpenGradeModal('EDIT');
                }
              : undefined
          }
          onDelete={
            !isViewer
              ? (g: Grade) => {
                  handleDelete(g.grade_id);
                }
              : undefined
          }
          onMerge={isAdmin ? (g: Grade) => handleOpenMergeModal(g) : undefined}
        />
      </div>

      {/* Grade Modal */}
      {selectedGrade && (
        <GradeModal
          grade={selectedGrade}
          initialMode={openGradeModal || 'VIEW'}
          save={handleSaveGrade}
          close={handleCloseGradeModal}
        />
      )}

      {/* GenericMergeModal for Grades */}
      {openMergeModal && mergeSourceGrade && (
        <GenericMergeModal<Grade>
          source={mergeSourceGrade}
          allEntities={grades}
          config={{
            domainDisplayName: 'Product Type',
            domainDisplayNamePlural: 'Product Types',
            entityIdField: 'grade_id',
            entityNameField: 'grade_name',
            entityCreatedDateField: 'created_date',
            useMergeSuggestions,
            useMergePreview,
            useMerge: useMergeGrades,
          }}
          onClose={handleCloseMergeModal}
          onSuccess={handleMergeSuccess}
        />
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  searchUploadContainerStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  } as React.CSSProperties,
  titleStyle: {
    fontSize: '1.25rem', // text-xl
    fontWeight: 'bold',
  } as React.CSSProperties,
  addButtonStyle: {
    backgroundColor: '#2563eb',
    color: 'white',
    padding: '0.25rem 0.75rem',
    borderRadius: '0.25rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.8125rem',
  } as React.CSSProperties,
  pageContainerStyle: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  } as React.CSSProperties,
  searchBarContainerStyle: {
    flexShrink: 0,
    backgroundColor: '#ffffff',
    borderBottom: `1px solid ${GRAY_300}`,
    paddingBottom: '8px',
  } as React.CSSProperties,
  tableContainerStyle: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0,
  } as React.CSSProperties,
};

export default Grades;
